import { Text, Colors, Card } from '@amelio/ui-library';
import { useState, useCallback, useRef } from 'react';

import { CompetencyGroupAccordion } from './CompetencyGroupAccordion';
import { ProficiencyLevelsList } from './ProficiencyLevelsList';

import type { AssessmentHistoryModalRef } from '@/pages/shared/HistoryModals/AssessmentHistoryModal';
import type { SkillProficiencyModalRef } from '@/pages/shared/HistoryModals/SkillProficiencyModal';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyGroupResult } from '@/types/manager-assessment/Competency';
import type { ProficiencyLevel as PerformanceProficiencyLevel } from '@/types/performance-profile';
import type { CompetencyResult } from '@/types/review/CompetencyGroupResult';

import { queryClient } from '@/AppWithProviders';
import { CompetencyProfileQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { mapCompetencyToSkillItem } from '@/pages/ManagerAssessment/utils/assessmentResultUtils';
import { AssessmentHistoryModal } from '@/pages/shared/HistoryModals/AssessmentHistoryModal';
import { SkillProficiencyModal } from '@/pages/shared/HistoryModals/SkillProficiencyModal';

interface CompetencyAssessmentContentProps {
    cycleInstanceId: string;
    employeeId?: string;
    onScoreSaved?: () => void;
    proficiencyLevels: ProficiencyLevel[];
    competencyGroup?: CompetencyGroupResult[];
    employeeName: string;
    isReadOnly?: boolean;
    updateSkillProficiency: (params: {
        skillId: string;
        proficiency: PerformanceProficiencyLevel;
        comment: string;
        cycleInstanceId: string;
    }) => Promise<void>;
    isUpdatingSkillProficiency: boolean;
}

export const CompetencyAssessmentContent = ({
    cycleInstanceId,
    employeeId,
    onScoreSaved,
    proficiencyLevels,
    competencyGroup,
    employeeName,
    isReadOnly = false,
    updateSkillProficiency,
    isUpdatingSkillProficiency,
}: CompetencyAssessmentContentProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

    // Refs for shared modals
    const skillProficiencyModalRef = useRef<SkillProficiencyModalRef>(null);
    const assessmentHistoryModalRef = useRef<AssessmentHistoryModalRef>(null);

    const toggleCategory = useCallback((categoryId: string) => {
        setOpenCategories((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    }, []);

    const handleEditClick = useCallback(
        (competency: CompetencyResult, e: React.MouseEvent) => {
            e.stopPropagation();
            if (isReadOnly) {
                return;
            }
            const skillItem = mapCompetencyToSkillItem(
                competency,
                cycleInstanceId,
                proficiencyLevels,
            );
            skillProficiencyModalRef.current?.open(skillItem);
        },
        [cycleInstanceId, isReadOnly, proficiencyLevels],
    );

    const handleHistoryClick = useCallback(
        (competency: CompetencyResult) => {
            const skillItem = mapCompetencyToSkillItem(
                competency,
                cycleInstanceId,
                proficiencyLevels,
            );
            assessmentHistoryModalRef.current?.open(skillItem);
        },
        [cycleInstanceId, proficiencyLevels],
    );

    const handleSaveProficiency = useCallback(
        async (skillId: string, proficiency: PerformanceProficiencyLevel, comment: string) => {
            if (isReadOnly) {
                return;
            }

            try {
                await updateSkillProficiency({
                    skillId,
                    proficiency,
                    comment,
                    cycleInstanceId: cycleInstanceId || '',
                });

                skillProficiencyModalRef.current?.close();
                queryClient.invalidateQueries({
                    queryKey: [CompetencyProfileQueryKey.PROFILE, 'by-employee', employeeId],
                });
                onScoreSaved?.();
            } catch (error) {
                handleError({ error });
            }
        },
        [updateSkillProficiency, handleError, onScoreSaved, cycleInstanceId, isReadOnly],
    );

    return (
        <Card
            className={'p-6'}
            bgColor={'bg-white'}
            borderRadius={'xl'}
        >
            <div className={'m-2 space-y-6'}>
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    className={'text-base'}
                >
                    {t('postReviewResults.competencies.assessment.description', {
                        employeeName: employeeName || '',
                    })}
                </Text>

                <div className={'space-y-4'}>
                    {competencyGroup?.map((group) => {
                        const isOpen = openCategories.has(group.id);
                        return (
                            <>
                                <CompetencyGroupAccordion
                                    key={group.id}
                                    group={group}
                                    isOpen={isOpen}
                                    employeeId={employeeId}
                                    proficiencyLevels={proficiencyLevels}
                                    isReadOnly={isReadOnly}
                                    onToggleCategory={toggleCategory}
                                    onHistoryClick={handleHistoryClick}
                                    onEditClick={handleEditClick}
                                />
                            </>
                        );
                    })}
                </div>

                <ProficiencyLevelsList proficiencyLevels={proficiencyLevels} />
            </div>

            <SkillProficiencyModal
                ref={skillProficiencyModalRef}
                onSave={handleSaveProficiency}
                isSaving={isUpdatingSkillProficiency}
                dataTestId={'skill-proficiency-modal'}
            />

            <AssessmentHistoryModal
                ref={assessmentHistoryModalRef}
                employeeId={employeeId}
                dataTestId={'assessment-history-modal'}
            />
        </Card>
    );
};
